import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// UX-FD-01 §0: a dedicated, lighter data context for the Front Desk shell — deliberately
// separate from AppDataContext (that one loads the org-wide feature-entitlement map the Owner
// Cockpit needs; Front Desk only ever needs its own `/me` + its own `branch_scope`). Also
// enforces the mirror-image of AppDataContext's kairoo-redirect: a non-FD session that lands on
// `/desk/*` (e.g. a stale bookmark, or a Manager/Super-Admin poking around) is bounced to the
// Owner Cockpit instead of ever rendering this shell — satisfies the prompt's own "MGR-only
// screens absent, not just disabled" rule in the OPPOSITE direction (FD-only shell absent for
// everyone else).
// UX-FD-02 §1 "Retail nav absent if the module is off, not just disabled": `entitlements` is
// now loaded here (not per-screen) so both the shell's nav AND any screen can gate on it once.
const DeskDataContext = createContext(null);

export function DeskDataProvider({ children }) {
  const [me, setMe] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [entitlements, setEntitlements] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readOnlyLapse, setReadOnlyLapse] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await api.get("/me");
      if (meRes.data.user_type === "kairoo") {
        navigate("/kairoo/dashboard");
        return;
      }
      // UX-FD-03 §1: Manager is FD's superset and now shares this same shell (gets an extra
      // nav section DeskShell gates on role) — anyone else (SA, trainer) still bounces to the
      // Owner Cockpit, mirroring AppDataContext's own opposite-direction bounce.
      if (meRes.data.role === "trainer") {
        navigate("/trainer/today");
        return;
      }
      if (!["front_of_house_fd", "front_of_house_mgr"].includes(meRes.data.role)) {
        navigate("/app/dashboard");
        return;
      }
      const [branchRes, entRes] = await Promise.all([api.get("/branches"), api.get("/feature-entitlements")]);
      setMe(meRes.data);
      setBranches(branchRes.data.branches);
      setEntitlements(entRes.data.entitlements);
      setBranchId((meRes.data.branch_scope || [])[0] || branchRes.data.branches[0]?.id || null);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/login");
        return;
      }
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // UX-FD-02 §1 "read-only-lapse blocks new sales/expenses, allows viewing" — same reactive,
  // event-driven pattern as AppDataContext (no proactive license-check endpoint exists).
  useEffect(() => {
    const handler = () => setReadOnlyLapse(true);
    window.addEventListener("fitfrik:read-only-lapse", handler);
    return () => window.removeEventListener("fitfrik:read-only-lapse", handler);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    navigate("/login");
  }, [navigate]);

  return (
    <DeskDataContext.Provider value={{ me, branches, branchId, setBranchId, entitlements, loading, error, readOnlyLapse, reload: load, logout }}>
      {children}
    </DeskDataContext.Provider>
  );
}

export function useDeskData() {
  return useContext(DeskDataContext);
}
